import { useMemo, useState } from "react";
import { toast } from "sonner";
import { api, apiErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileDropzone } from "@/components/FileDropzone";
import { Loader2, ScanSearch } from "lucide-react";

export const REQUEST_TYPES = [
  { value: "court", label: "Court matter" },
  { value: "loan", label: "Loan matter" },
  { value: "auto_loan", label: "Auto loan" },
  { value: "utility", label: "Utility matter" },
  { value: "birth_certificate", label: "Birth certificate" },
  { value: "traffic_ticket", label: "Traffic ticket" },
  { value: "warrant", label: "Warrant" },
  { value: "drivers_license", label: "Driver's license" },
  { value: "passport", label: "Passport" },
];

const UTILITY_TYPE_OPTIONS = [
  { value: "water", label: "Water" },
  { value: "sewer", label: "Sewer" },
  { value: "gas", label: "Gas" },
  { value: "electric", label: "Electric" },
];

// Per-matter-type fields matching the owner's Google Sheet extraction schema.
const TYPE_CONFIG = {
  court: {
    identifierDefault: "case",
    identifierOptions: null,
    documentKinds: [
      { value: "complaint_first_page", label: "First page of the complaint" },
      { value: "court_matter_first_page", label: "First page of the court matter" },
    ],
    identifierPlaceholder: "2024-CA-001234",
    extraFields: [
      { key: "courtDistrict", label: "Court jurisdiction", type: "text", placeholder: "In the Circuit Court of the Eleventh Judicial District" },
      { key: "caseFiledDate", label: "Original filing/recording date", type: "date" },
    ],
  },
  loan: {
    identifierDefault: "loan",
    identifierOptions: [
      { value: "loan", label: "Loan number (promissory note / 1st page of mortgage)" },
      { value: "account", label: "Account number (mortgage billing statement)" },
    ],
    documentKinds: [
      { value: "promissory_note", label: "Promissory note" },
      { value: "loan_statement", label: "Monthly loan statement" },
    ],
    identifierPlaceholder: "LN-778899-X",
    extraFields: [
      { key: "city", label: "City where the note was executed", type: "text", placeholder: "Naples" },
      { key: "accountNumber", label: "Account number (from billing statement — optional)", type: "text", placeholder: "ACCT-001122", optional: true },
    ],
  },
  auto_loan: {
    identifierDefault: "loan",
    identifierOptions: [
      { value: "loan", label: "Loan number (retail installment contract)" },
      { value: "account", label: "Account number (billing statement)" },
    ],
    documentKinds: [
      { value: "auto_loan_contract", label: "Retail installment contract" },
      { value: "auto_loan_statement", label: "Monthly billing statement" },
    ],
    identifierPlaceholder: "AL-445566-Z",
    extraFields: [
      { key: "city", label: "City where the contract was executed", type: "text", placeholder: "Naples" },
      { key: "accountNumber", label: "Account number (from billing statement — optional)", type: "text", placeholder: "ACCT-003344", optional: true },
    ],
  },
  utility: {
    identifierDefault: "account",
    identifierOptions: null,
    documentKinds: [{ value: "utility_statement", label: "Monthly utility statement" }],
    identifierPlaceholder: "UT-123456",
    extraFields: [
      { key: "utilityCompany", label: "Name of the utility company", type: "text", placeholder: "City Water & Power" },
      { key: "utilityType", label: "Type of utility", type: "select", options: UTILITY_TYPE_OPTIONS },
    ],
  },
  birth_certificate: {
    identifierDefault: "certificate",
    identifierOptions: null,
    documentKinds: [{ value: "birth_certificate", label: "Copy of the birth certificate" }],
    identifierPlaceholder: "State file / file number",
    extraFields: [
      { key: "dateOfBirth", label: "Date of birth", type: "date" },
      { key: "dateFiled", label: "Date filed / recorded by registrar", type: "date" },
    ],
  },
  traffic_ticket: {
    identifierDefault: "ticket",
    identifierOptions: null,
    documentKinds: [{ value: "traffic_ticket", label: "Copy of the ticket / citation" }],
    identifierPlaceholder: "Citation number",
    extraFields: [
      { key: "ticketDate", label: "Date of the ticket", type: "date" },
      { key: "city", label: "City", type: "text", placeholder: "Naples" },
    ],
  },
  warrant: {
    identifierDefault: "warrant",
    identifierOptions: null,
    documentKinds: [{ value: "warrant", label: "Copy of the warrant" }],
    identifierPlaceholder: "Warrant number (as listed on the warrant)",
    extraFields: [
      { key: "issueDate", label: "Date warrant issued", type: "date" },
      { key: "courtDistrict", label: "Court jurisdiction", type: "text", placeholder: "County Court of Collier County" },
      { key: "city", label: "City", type: "text", placeholder: "Naples" },
    ],
  },
  drivers_license: {
    identifierDefault: "dl",
    identifierOptions: null,
    documentKinds: [{ value: "drivers_license", label: "Copy of the driver's license (front)" }],
    identifierPlaceholder: "DL number (as printed on the license)",
    hideCounty: true,
    stateLabel: "State issued",
    statePlaceholder: "IL or Illinois",
    extraFields: [
      { key: "address", label: "Address (as shown on the license)", type: "text", placeholder: "123 Main St, Springfield, IL 62701" },
      { key: "dateOfBirth", label: "Date of birth", type: "date" },
      { key: "expirationDate", label: "Date of expiration", type: "date" },
    ],
  },
  passport: {
    identifierDefault: "passport",
    identifierOptions: null,
    documentKinds: [{ value: "passport", label: "Copy of the passport photo page" }],
    identifierPlaceholder: "Passport number",
    hideState: true,
    hideCounty: true,
    extraFields: [
      { key: "expirationDate", label: "Date of expiration", type: "date" },
    ],
  },
};

const IDENTIFIER_LABELS = {
  case: "Case number",
  loan: "Loan number",
  account: "Account number",
  certificate: "Certificate number",
  ticket: "Ticket / citation number",
  warrant: "Warrant number",
  dl: "Driver's license number",
  passport: "Passport number",
};

const EMPTY_FORM = {
  firstName: "",
  lastName: "",
  identifierType: "case",
  identifierValue: "",
  state: "",
  county: "",
  courtDistrict: "",
  caseFiledDate: "",
  documentKind: "complaint_first_page",
  city: "",
  accountNumber: "",
  utilityCompany: "",
  utilityType: "",
  dateOfBirth: "",
  dateFiled: "",
  ticketDate: "",
  issueDate: "",
  address: "",
  expirationDate: "",
};

const EXTRA_KEYS = ["courtDistrict", "caseFiledDate", "city", "accountNumber", "utilityCompany", "utilityType", "dateOfBirth", "dateFiled", "ticketDate", "issueDate", "address", "expirationDate"];

export const OrderForm = ({ onCreated }) => {
  const [requestType, setRequestType] = useState("court");
  const [form, setForm] = useState(EMPTY_FORM);
  const [file, setFile] = useState(null);
  const [certified, setCertified] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const config = TYPE_CONFIG[requestType];

  const set = (key) => (value) => setForm((previous) => ({ ...previous, [key]: value }));
  const setInput = (key) => (event) => set(key)(event.target.value);

  const switchType = (type) => {
    setRequestType(type);
    setForm((previous) => {
      const next = { ...previous };
      EXTRA_KEYS.forEach((key) => {
        next[key] = "";
      });
      next.identifierType = TYPE_CONFIG[type].identifierDefault;
      next.documentKind = TYPE_CONFIG[type].documentKinds[0].value;
      return next;
    });
  };

  const missingFields = useMemo(() => {
    const missing = [];
    if (!form.firstName.trim()) missing.push("First name");
    if (!form.lastName.trim()) missing.push("Last name");
    if (!form.identifierValue.trim()) missing.push(IDENTIFIER_LABELS[form.identifierType]);
    if (!config.hideState && !form.state.trim()) missing.push(config.stateLabel || "State");
    if (!config.hideCounty && !form.county.trim()) missing.push("County");
    config.extraFields.forEach((field) => {
      if (!field.optional && !String(form[field.key] || "").trim()) missing.push(field.label);
    });
    if (!file) missing.push("Supporting document");
    if (!certified) missing.push("Upload certification");
    return missing;
  }, [form, file, config, certified]);

  const submit = async (event) => {
    event.preventDefault();
    if (missingFields.length) {
      toast.error(`Please complete: ${missingFields.join(", ")}`);
      return;
    }
    setSubmitting(true);
    const body = new FormData();
    body.append("document", file);
    body.append("firstName", form.firstName);
    body.append("lastName", form.lastName);
    body.append("requestType", requestType);
    body.append("identifierType", form.identifierType);
    body.append("identifierValue", form.identifierValue);
    body.append("state", form.state);
    body.append("county", form.county);
    body.append("courtDistrict", form.courtDistrict);
    body.append("caseFiledDate", form.caseFiledDate);
    body.append("documentKind", form.documentKind);
    body.append("city", form.city);
    body.append("accountNumber", form.accountNumber);
    body.append("utilityCompany", form.utilityCompany);
    body.append("utilityType", form.utilityType);
    body.append("dateOfBirth", form.dateOfBirth);
    body.append("dateFiled", form.dateFiled);
    body.append("ticketDate", form.ticketDate);
    body.append("issueDate", form.issueDate);
    body.append("address", form.address);
    body.append("expirationDate", form.expirationDate);
    try {
      const { data } = await api.post("/orders", body, { timeout: 120000 });
      toast.success(
        "Document uploaded — extraction and comparison are running in the background. " +
          "You'll be asked to review and confirm the extracted information in a moment."
      );
      setForm(EMPTY_FORM);
      setFile(null);
      setCertified(false);
      switchType("court");
      onCreated?.(data);
    } catch (error) {
      toast.error(apiErrorMessage(error, "Order submission failed."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card className="bg-card border-border" data-testid="order-cusip-card">
      <CardHeader>
        <CardTitle className="font-display text-xl flex items-center gap-2">
          <ScanSearch className="h-5 w-5 text-primary" />
          Order Your QSIP
        </CardTitle>
        <CardDescription>
          Submit one matter with one supporting document. Our extraction engine reads the document data and
          compares it with your entry before checkout.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={submit} className="space-y-5" noValidate>
          <div className="space-y-1.5">
            <Label>Matter type</Label>
            <Select value={requestType} onValueChange={switchType}>
              <SelectTrigger data-testid="order-request-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {REQUEST_TYPES.map((type) => (
                  <SelectItem key={type.value} value={type.value} data-testid={`order-type-${type.value}-option`}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="order-first-name">First name</Label>
              <Input id="order-first-name" data-testid="order-first-name-input" value={form.firstName} onChange={setInput("firstName")} placeholder="John" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="order-last-name">Last name</Label>
              <Input id="order-last-name" data-testid="order-last-name-input" value={form.lastName} onChange={setInput("lastName")} placeholder="Smith" />
            </div>

            <div className="space-y-1.5">
              <Label>Identifier type</Label>
              {config.identifierOptions ? (
                <Select key={requestType} value={form.identifierType} onValueChange={set("identifierType")}>
                  <SelectTrigger data-testid="order-identifier-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {config.identifierOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input value={IDENTIFIER_LABELS[form.identifierType]} disabled data-testid="order-identifier-type-fixed" />
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="order-identifier">{IDENTIFIER_LABELS[form.identifierType]}</Label>
              <Input
                id="order-identifier"
                data-testid="order-identifier-value-input"
                className="font-mono"
                value={form.identifierValue}
                onChange={setInput("identifierValue")}
                placeholder={config.identifierPlaceholder}
              />
            </div>

            {!config.hideState && (
              <div className="space-y-1.5">
                <Label htmlFor="order-state">{config.stateLabel || "State"}</Label>
                <Input id="order-state" data-testid="order-state-input" value={form.state} onChange={setInput("state")} placeholder={config.statePlaceholder || "FL or Florida"} />
              </div>
            )}
            {!config.hideCounty && (
              <div className="space-y-1.5">
                <Label htmlFor="order-county">County</Label>
                <Input id="order-county" data-testid="order-county-input" value={form.county} onChange={setInput("county")} placeholder="Miami-Dade" />
              </div>
            )}

            {config.extraFields.map((field) => (
              <div key={field.key} className="space-y-1.5">
                <Label htmlFor={`order-${field.key}`}>{field.label}</Label>
                {field.type === "select" ? (
                  <Select value={form[field.key]} onValueChange={set(field.key)}>
                    <SelectTrigger id={`order-${field.key}`} data-testid={`order-${field.key}-select`}>
                      <SelectValue placeholder="Select…" />
                    </SelectTrigger>
                    <SelectContent>
                      {field.options.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    id={`order-${field.key}`}
                    type={field.type === "date" ? "date" : "text"}
                    data-testid={`order-${field.key}-input`}
                    value={form[field.key]}
                    onChange={setInput(field.key)}
                    placeholder={field.placeholder || ""}
                  />
                )}
              </div>
            ))}

            <div className="space-y-1.5 md:col-span-2">
              <Label>Supporting document type</Label>
              <Select key={requestType} value={form.documentKind} onValueChange={set("documentKind")}>
                <SelectTrigger data-testid="order-document-kind-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {config.documentKinds.map((kind) => (
                    <SelectItem key={kind.value} value={kind.value}>
                      {kind.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <FileDropzone file={file} onFileChange={setFile} />
          <p className="text-[11px] text-muted-foreground leading-relaxed" data-testid="order-redaction-notice">
            Upload only what is necessary. Please redact or mask full Social Security numbers and full financial
            account numbers unless a specific identifier is required for your report. Files are processed
            automatically (including OCR and AI-assisted extraction) to prepare your informational report.
          </p>

          <div className="flex items-start gap-2.5 rounded-lg border border-border bg-secondary/40 p-3.5">
            <Checkbox
              id="order-certification"
              checked={certified}
              onCheckedChange={(value) => setCertified(value === true)}
              data-testid="order-certification-checkbox"
            />
            <Label htmlFor="order-certification" className="text-xs leading-relaxed cursor-pointer">
              I certify that I am authorized to upload this document, that the information I entered is accurate
              to the best of my knowledge, and I authorize GetYourQSIP to process this document to prepare my
              informational report. I understand reports are informational only, are not legal, financial, or
              professional advice, and do not guarantee any outcome.
            </Label>
          </div>

          <Button type="submit" className="w-full" size="lg" disabled={submitting} data-testid="order-cusip-submit-button">
            {submitting ? (
              <span className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" /> Uploading document…
              </span>
            ) : (
              "Complete, Extract & Compare"
            )}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};
